// src/DataContext.tsx
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Client,
  Project,
  Expense,
  SubContract,
  PaymentReceived,
  ScopeItem,
  SubContractor,
  Resource,
  WageEntry,
  Partner,
  PartnerDisbursement,
  ProjectTransfer,
  defaultPartners,
} from "./data/mockData";

import {
  computeProjectFinancialsEngine,
  EngineResult,
} from "./engine/projectFinancialsEngine";

import { getStoredTenantId, setStoredTenantId } from "./tenant";

// ✅ Projects API (D1-backed)
import {
  listProjects,
  createProject,
  updateProjectById,
  apiProjectToUi,
  uiProjectToApi,
} from "./api/projects";

// ✅ generic http helper (already in your app)
import { apiRequest } from "./api/http";

/* ======================
   Types
====================== */

type EnrichedProject = Project & {
  totalEstimate: number;
  received: number;
  balance: number;
  status: string;
};

type EnrichedSubContract = SubContract & {
  paidToDate: number;
  balance: number;
};

export type Role = "tenant" | "admin";

export type ModuleKey =
  | "core"
  | "clients"
  | "projects"
  | "expenses"
  | "subcontracts"
  | "payments"
  | "resources"
  | "wages"
  | "partners"
  | "invoices";

export type Entitlements = {
  enabled: Record<ModuleKey, boolean>;
};

export type CompanyInfo = {
  legalName: string;            // only required
  dba?: string;
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
  logoUrl?: string;
  taxId?: string;
  licenseNumber?: string;
  notes?: string;               // footer note
  updatedAt?: string;
};

export type TenantProfile = {
  tenantId: string; // slug
  tenantName: string;
  createdAt: string;
};

export type AuthSession = {
  role: Role;
  tenantId: string; // for tenant: required; for admin: last selected tenant scope
  createdAt: string;
};

export type StorageDriver = {
  load<T>(key: string, fallback: T): T;
  save<T>(key: string, data: T): void;
  remove(key: string): void;
  clear(keys: string[]): void;
};

type DataContextType = {

  // ✅ Force re-hydration from API so ProjectDetailPage totals update immediately
  refreshProjectData: (projectId: string) => Promise<void>;

  // ✅ API tenant header scope (for D1-backed API calls)
  tenantId: string;
  setTenantId: (id: string) => void;

  // Auth
  authSession: AuthSession | null;
  setAuthSession: (s: AuthSession) => void;
  clearAuthSession: () => void;

  // System-admin tenant list (DB-backed)
  systemTenants: { id: string; name: string; inviteCode: string }[];
  reloadSystemTenantsFromApi: () => Promise<void>;

  //Company info 
  companyInfo: CompanyInfo;
  setCompanyInfo: (c: CompanyInfo) => void;

  // Tenant registry
  tenants: TenantProfile[];
  activeTenantId: string;
  activeTenant: TenantProfile;

  createTenant: (name: string, desiredId?: string) => void;
  renameTenant: (tenantId: string, newName: string) => void;
  deleteTenant: (tenantId: string) => void;
  switchTenant: (tenantId: string) => void;

  // Invites
  createInvite: (tenantId: string) => string;
  consumeInvite: (code: string) => { tenantId: string };

  // Entitlements (per tenant scope)
  entitlements: Entitlements;
  setEntitlements: (e: Entitlements) => void;
  hasModule: (m: ModuleKey) => boolean;

  // Partners (per tenant scope) — API-backed (D1) + local fallback cache
  partners: Partner[];
  setPartners: (p: Partner[]) => void; // kept for backward-compat
  addPartner: (p: Partner) => void;
  updatePartner: (p: Partner) => void;
  deletePartner: (partnerId: string) => void;

  // Raw / base data (per tenant scope)
  clients: Client[];
  projects: EnrichedProject[];
  expenses: Expense[];
  subContracts: EnrichedSubContract[];
  paymentsReceived: PaymentReceived[];
  scopeItems: ScopeItem[];
  subContractors: SubContractor[];

  // ✅ API-backed
  resources: Resource[];
  reloadResourcesFromApi: () => Promise<void>;

  reloadScopeFromApi: (projectIds: string[]) => Promise<void>;
  reloadPaymentsFromApi: () => Promise<void>;
  reloadExpensesFromApi: () => Promise<void>;
  reloadWagesFromApi: () => Promise<void>;
  reloadPartnerDisbursementsFromApi: () => Promise<void>;
  reloadProjectTransfersFromApi: () => Promise<void>;
  reloadSubContractsFromApi: () => Promise<void>;


  // ✅ Wages API-backed
  wageEntries: WageEntry[];

  // ✅ Partner disbursements API-backed
  partnerDisbursements: PartnerDisbursement[];
  projectTransfers: ProjectTransfer[];

  // Engine outputs
  projectFinancialsByProject: EngineResult["byProjectId"];
  projectOrder: EngineResult["projectOrder"];
  partnerLedgerFinal: EngineResult["partnerLedgerFinal"];

  // Actions
  addClient: (c: Client) => void;
  updateClient: (c: Client) => void;
  deleteClient: (clientId: string) => void;

  addProject: (p: Project) => void;
  updateProject: (p: Project) => void;
  archiveProject: (projectId: string) => void;
  restoreProject: (projectId: string) => void;

  addExpense: (e: Expense) => void;
  updateExpense: (e: Expense) => void;
  deleteExpense: (expenseId: string) => void;

  addSubContract: (s: SubContract) => void;
  updateSubContract: (s: SubContract) => void;
  deleteSubContract: (subContractId: string) => void;

  addPaymentReceived: (p: PaymentReceived) => void;
  updatePaymentReceived: (p: PaymentReceived) => void;
  deletePaymentReceived: (paymentId: string) => void;

  addScopeItem: (s: ScopeItem) => void;
  updateScopeItem: (s: ScopeItem) => void;
  deleteScopeItem: (scopeItemId: string) => void;

  addSubContractor: (s: SubContractor) => void;
  updateSubContractor: (s: SubContractor) => void;
  deleteSubContractor: (subContractorId: string) => void;

  addResource: (r: Resource) => void;
  updateResource: (r: Resource) => void;
  deleteResource: (resourceId: string) => void;

  addWageEntry: (w: WageEntry) => void;
  updateWageEntry: (w: WageEntry) => void;
  deleteWageEntry: (wageId: string) => void;

  addPartnerDisbursement: (d: PartnerDisbursement) => void;
  updatePartnerDisbursement: (d: PartnerDisbursement) => void;
  deletePartnerDisbursement: (id: string) => void;

  resetAllData: () => void;
  deleteProjectAndRelated: (projectId: string) => void;

  // Active (tenant-scoped) driver
  storageDriver: StorageDriver;
};

const DataContext = createContext<DataContextType | undefined>(undefined);

/* ======================
   Defaults
====================== */

const MODULE_KEYS: ModuleKey[] = [
  "core",
  "clients",
  "projects",
  "expenses",
  "subcontracts",
  "payments",
  "resources",
  "wages",
  "partners",
  "invoices",
];

function defaultEntitlements(): Entitlements {
  return {
    enabled: {
      core: true,
      clients: true,
      projects: true,
      expenses: true,
      subcontracts: true,
      payments: true,
      resources: true,
      wages: true,
      partners: true,
      invoices: true,
    },
  };
}

/** Merge loaded entitlements with defaults and drop unknown keys (forward/backward safe). */
function normalizeEntitlements(input: any): Entitlements {
  const base = defaultEntitlements();
  const out: Entitlements = { enabled: { ...base.enabled } };

  const enabled = input?.enabled || {};
  MODULE_KEYS.forEach((k) => {
    if (k === "core") out.enabled.core = true;
    else if (typeof enabled[k] === "boolean") out.enabled[k] = enabled[k];
  });

  return out;
}

function defaultTenantList(): TenantProfile[] {
  return [
    {
      tenantId: "demo",
      tenantName: "Demo Tenant",
      createdAt: new Date().toISOString(),
    },
  ];
}

/* ======================
   Drivers
====================== */

function createLocalStorageDriver(): StorageDriver {
  const hasWindow =
    typeof window !== "undefined" &&
    typeof window.localStorage !== "undefined" &&
    window.localStorage !== null;

  if (!hasWindow) {
    const mem = new Map<string, string>();
    return {
      load<T>(key: string, fallback: T): T {
        try {
          const saved = mem.get(key);
          return saved ? (JSON.parse(saved) as T) : fallback;
        } catch {
          return fallback;
        }
      },
      save<T>(key: string, data: T) {
        try {
          mem.set(key, JSON.stringify(data));
        } catch {
          /* no-op */
        }
      },
      remove(key: string) {
        mem.delete(key);
      },
      clear(keys: string[]) {
        keys.forEach((k) => mem.delete(k));
      },
    };
  }

  return {
    load<T>(key: string, fallback: T): T {
      try {
        const saved = window.localStorage.getItem(key);
        return saved ? (JSON.parse(saved) as T) : fallback;
      } catch {
        return fallback;
      }
    },
    save<T>(key: string, data: T) {
      try {
        window.localStorage.setItem(key, JSON.stringify(data));
      } catch {
        /* no-op */
      }
    },
    remove(key: string) {
      try {
        window.localStorage.removeItem(key);
      } catch {
        /* no-op */
      }
    },
    clear(keys: string[]) {
      keys.forEach((k) => {
        try {
          window.localStorage.removeItem(k);
        } catch {
          /* no-op */
        }
      });
    },
  };
}

function createScopedDriver(base: StorageDriver, tenantId: string): StorageDriver {
  const prefix = `njdc__t__${tenantId}__`;
  const k = (key: string) => `${prefix}${key}`;

  return {
    load<T>(key: string, fallback: T): T {
      return base.load<T>(k(key), fallback);
    },
    save<T>(key: string, data: T) {
      base.save<T>(k(key), data);
    },
    remove(key: string) {
      base.remove(k(key));
    },
    clear(keys: string[]) {
      base.clear(keys.map((x) => k(x)));
    },
  };
}

/* ======================
   Utils
====================== */

function slugify(input: string) {
  const s = (input || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return s || "tenant";
}

function ensureUniqueTenantId(desired: string, existing: TenantProfile[]): string {
  const base = slugify(desired);

  const ids = new Set(existing.map((t) => t.tenantId));
  if (!ids.has(base)) return base;

  let i = 2;
  while (ids.has(`${base}-${i}`)) i++;

  return `${base}-${i}`;
}

/* ======================
   Provider
====================== */

export function DataProvider({ children }: { children: React.ReactNode }) {
  const baseDriver = useMemo(() => createLocalStorageDriver(), []);

  // ✅ Tenant id for API header scope
  const [tenantId, setTenantIdState] = useState<string>(() => getStoredTenantId());
  const setTenantId = (next: string) => {
    const clean = (next || "").trim() || "demo";
    setTenantIdState(clean);
    setStoredTenantId(clean);
  };

  // Global keys (NOT tenant-scoped)
  const GLOBAL = useMemo(
    () => ({
      tenants: "njdc_tenants",
      activeTenant: "njdc_active_tenant",
      auth: "njdc_auth_session",
      invites: "njdc_invites", // map inviteCode -> tenantId
    }),
    []
  );

  // Auth session
  const [authSession, _setAuthSession] = useState<AuthSession | null>(() =>
    baseDriver.load<AuthSession | null>(GLOBAL.auth, null)
  );

  const setAuthSession = (s: AuthSession) => {
    _setAuthSession(s);
    baseDriver.save(GLOBAL.auth, s);
  };

  const clearAuthSession = () => {
    _setAuthSession(null);
    baseDriver.remove(GLOBAL.auth);
  };

  /* ======================
     ✅ AUTH: hydrate from DB session cookie (source of truth)
     Uses GET /api/system/auth/me
  ====================== */

  type AuthMeOk = {
    ok: true;
    data: {
      signedIn: true;
      role: Role; // "admin" | "tenant"
      tenantId: string; // authenticated tenant (system for sysadmin)
      user: { userId: string; username: string };
      createdAt: string;
    };
  };

  const didAuthHydrateRef = useRef(false);

  useEffect(() => {
    if (didAuthHydrateRef.current) return;
    didAuthHydrateRef.current = true;

    (async () => {
      try {
        const res = await fetch("/api/auth/me", {
          method: "GET",
          credentials: "include",
        }).then((r) => r.json());

        if ((res as any)?.ok && (res as any)?.data?.signedIn) {
          const s = (res as any).data as AuthMeOk["data"];

          const next: AuthSession = {
            role: (s.role as Role) || "tenant",
            // IMPORTANT: tenantId here should reflect the authenticated tenant from the session cookie
            // - sysadmin => "system"
            // - tenant users => their tenant
            tenantId: (s.tenantId || "demo").trim() || "demo",
            createdAt: s.createdAt || new Date().toISOString(),
          };

          _setAuthSession(next);
          baseDriver.save(GLOBAL.auth, next);

          // Align tenant scope immediately so API calls and UI scope match the authenticated session
          setActiveTenantId(next.tenantId);
          setStoredTenantId(next.tenantId);

          return;

        }

        // Not signed in
        _setAuthSession(null);
        baseDriver.remove(GLOBAL.auth);
      } catch (e) {
        // Treat errors as not signed in (prevents UI from lying)
        _setAuthSession(null);
        baseDriver.remove(GLOBAL.auth);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tenant list
  const [tenants, setTenants] = useState<TenantProfile[]>(() => {
    const loaded = baseDriver.load<TenantProfile[]>(GLOBAL.tenants, []);
    return loaded.length ? loaded : defaultTenantList();
  });

  // Active tenant id
  const [activeTenantId, setActiveTenantId] = useState<string>(() => {
    const saved = baseDriver.load<string>(GLOBAL.activeTenant, "demo");
    const ok = tenants.some((t) => t.tenantId === saved);
    return ok ? saved : tenants[0]?.tenantId || "demo";
  });

  // Persist tenant list + active tenant globally
  useEffect(() => {
    baseDriver.save(GLOBAL.tenants, tenants);
  }, [baseDriver, GLOBAL, tenants]);

  useEffect(() => {
    baseDriver.save(GLOBAL.activeTenant, activeTenantId);
  }, [baseDriver, GLOBAL, activeTenantId]);

  // If TENANT role: force activeTenantId to their tenant
  useEffect(() => {
    if (authSession?.role === "tenant") {
      const target = authSession.tenantId;
      if (
        target &&
        tenants.some((t) => t.tenantId === target) &&
        target !== activeTenantId
      ) {
        setActiveTenantId(target);
      }
    }
  }, [authSession, tenants, activeTenantId]);

  // Active tenant record
  const activeTenant = useMemo(() => {
    return (
      tenants.find((t) => t.tenantId === activeTenantId) ||
      tenants[0] || {
        tenantId: "demo",
        tenantName: "Demo Tenant",
        createdAt: new Date().toISOString(),
      }
    );
  }, [tenants, activeTenantId]);

  // ✅ Scope rules:
  // - tenant users: ALWAYS scoped to their authenticated tenant
  // - admin: scoped to whichever tenant they selected in UI
  const scopeTenantId =
    authSession?.role === "tenant"
      ? (authSession.tenantId || "demo")
      : activeTenant.tenantId;


  // ✅ Keep API header tenantId aligned to current active tenant scope.
  useEffect(() => {
    if ((tenantId || "demo") !== scopeTenantId) {
      setTenantId(scopeTenantId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeTenantId]);

  // Tenant-scoped driver
  const storageDriver = useMemo(() => {
    return createScopedDriver(baseDriver, scopeTenantId);
  }, [baseDriver, scopeTenantId]);

  // Tenant-scoped keys
  const KEYS = useMemo(
    () => ({
      entitlements: "njdc_entitlements",
      partners: "njdc_partners",
      companyInfo: "njdc_company_info",

      clients: "njdc_clients",
      projects: "njdc_projects",
      expenses: "njdc_expenses",
      subcontracts: "njdc_subcontracts",
      paymentsReceived: "njdc_payments_received",
      scopeItems: "njdc_scope_items",
      subcontractors: "njdc_subcontractors",

      // keep as fallback only (resources + wages now API-backed)
      resources: "njdc_resources",
      wageEntries: "njdc_wage_entries",

      partnerDisbursements: "njdc_partner_disbursements",
    }),
    []
  );

  /* ======================
     Invites (global)
  ====================== */

  const createInvite = (tenantId: string) => {
    // Reusable invite = tenantId
    return (tenantId || "").trim();
  };

  const consumeInvite = (code: string): { tenantId: string } => {
    const raw = (code || "").trim();
    if (!raw) throw new Error("Enter your tenant code.");

    // Accept BOTH formats:
    // 1) "acme"
    // 2) "TENANT-acme-123456" (legacy)
    let tenantId = raw;

    const m = raw.match(/^TENANT-([a-zA-Z0-9-]+)-(\d{6})$/i);
    if (m) tenantId = (m[1] || "").trim();

    tenantId = tenantId.toLowerCase().trim();
    if (!tenantId) throw new Error("Invalid tenant code.");

    // activate tenant scope
    setStoredTenantId(tenantId);
    setActiveTenantId(tenantId);

    // Ensure tenant appears in local UI list (convenience only)
    setTenants((prev) => {
      const exists = prev.some((t) => t.tenantId === tenantId);
      if (exists) return prev;
      return [
        { tenantId, tenantName: tenantId, createdAt: new Date().toISOString() },
        ...prev,
      ];
    });
    return { tenantId };
  };

  /* ======================
     Tenant API
  ====================== */

  // src/DataContext.tsx

  const switchTenant = (tenantId: string) => {
    const id = (tenantId || "").trim();
    if (!id) return;

    // If tenant isn't in local list yet, add it (using DB list name if we have it)
    setTenants((prev) => {
      const exists = prev.some((t) => t.tenantId === id);
      if (exists) return prev;

      const dbHit = (systemTenants || []).find((t) => t.id === id);
      const name = dbHit?.name || id;

      return [{ tenantId: id, tenantName: name, createdAt: new Date().toISOString() }, ...prev];
    });

    // Switch scope
    setActiveTenantId(id);

    // DB-backed entitlements reload
    reloadEntitlementsFromApi(id);

    // For admin, also persist the selected tenant into auth session (scope preference)
    if (authSession?.role === "admin") {
      const next: AuthSession = { ...authSession, tenantId: id };
      _setAuthSession(next);
      baseDriver.save(GLOBAL.auth, next);
    }
  };


    const createTenant = (name: string, desiredId?: string) => {
      const id = ensureUniqueTenantId(desiredId || name || "tenant", tenants);
      const t: TenantProfile = {
        tenantId: id,
        tenantName: (name || "").trim() || id,
        createdAt: new Date().toISOString(),
      };

      setTenants((prev) => [...prev, t]);
      setActiveTenantId(id);

      if (authSession?.role === "admin") {
        const next: AuthSession = { ...authSession, tenantId: id };
        _setAuthSession(next);
        baseDriver.save(GLOBAL.auth, next);
      }
    };

  const renameTenant = (tenantId: string, newName: string) => {
    setTenants((prev) =>
      prev.map((t) =>
        t.tenantId === tenantId ? { ...t, tenantName: newName } : t
      )
    );
  };

  const deleteTenant = (tenantId: string) => {
    if (tenantId === "demo") {
      alert("Demo tenant cannot be deleted.");
      return;
    }
    const ok = window.confirm(
      `Delete tenant "${tenantId}"?\n\nThis will remove it from the tenant list.\n(Your browser may still retain scoped data unless you clear site data.)`
    );
    if (!ok) return;

    setTenants((prev) => prev.filter((t) => t.tenantId !== tenantId));

    if (activeTenantId === tenantId) {
      const next =
        tenants.find((t) => t.tenantId !== tenantId)?.tenantId || "demo";
      setActiveTenantId(next);

      if (authSession?.role === "admin") {
        const nextSession: AuthSession = { ...authSession, tenantId: next };
        _setAuthSession(nextSession);
        baseDriver.save(GLOBAL.auth, nextSession);
      }
    }
  };

  /* ======================
     Step 1: Tenant-scoped HYDRATION
  ====================== */

  const [hydratedScopeId, setHydratedScopeId] = useState<string>(scopeTenantId);
  const isHydratingRef = useRef(false);

  // Tenant-scoped states (local/fallback)
  const [entitlements, setEntitlements] = useState(() =>
    normalizeEntitlements(defaultEntitlements())
  );
  const [partners, setPartners] = useState<Partner[]>(() =>
    storageDriver.load<Partner[]>(KEYS.partners, defaultPartners)
  );

  const [companyInfo, setCompanyInfo] = useState<CompanyInfo>(() =>
    storageDriver.load<CompanyInfo>(KEYS.companyInfo, {
      legalName: "",
      updatedAt: new Date().toISOString(),
    })
  );

  // ✅ Clients are API-backed, but keep local fallback storage
  const [clients, setClients] = useState<Client[]>(() =>
    storageDriver.load<Client[]>(KEYS.clients, [])
  );

  const [projectsRaw, setProjectsRaw] = useState<Project[]>(() =>
    storageDriver.load(KEYS.projects, [])
  );
  const [expenses, setExpenses] = useState<Expense[]>(() =>
    storageDriver.load(KEYS.expenses, [])
  );

  // ✅ Subcontracts will be API-backed (but keep fallback storage)
  const [subContractsRaw, setSubContractsRaw] = useState<SubContract[]>(() =>
    storageDriver.load(KEYS.subcontracts, [])
  );
  
  const [paymentsReceived, setPaymentsReceived] = useState<PaymentReceived[]>(() =>
    storageDriver.load(KEYS.paymentsReceived, [])
  );
  const [scopeItems, setScopeItems] = useState<ScopeItem[]>(() =>
    storageDriver.load(KEYS.scopeItems, [])
  );
  const [subContractors, setSubContractors] = useState<SubContractor[]>(() =>
    storageDriver.load(KEYS.subcontractors, [
      { id: "SC-001", name: "Quinteros General Construction" },
      { id: "SC-002", name: "Benjamin B & B Painting" },
      { id: "SC-003", name: "Flooring Crew" },
      { id: "SC-004", name: "Electrician XYZ" },
    ])
  );

  // ✅ API-backed state (still stored as fallback too)
  const [resources, setResources] = useState<Resource[]>(() =>
    storageDriver.load<Resource[]>(KEYS.resources, [])
  );
  const [wageEntries, setWageEntries] = useState<WageEntry[]>(() =>
    storageDriver.load<WageEntry[]>(KEYS.wageEntries, [])
  );

  const [partnerDisbursements, setPartnerDisbursements] = useState<
    PartnerDisbursement[]
  >(() => storageDriver.load(KEYS.partnerDisbursements, []));
  const [projectTransfers, setProjectTransfers] = useState<ProjectTransfer[]>([]);

  // 🔥 On scope change, reload local-scoped items from the new scoped storage.
    /* ======================
     ✅ Tenant Modules (DB-backed entitlements)
     Source of truth: D1 table tenant_modules
  ====================== */

  type ApiOk<T> = { ok: true; data: T };
  type ApiErr = { ok: false; error: string };

  type ApiTenantModuleRow = {
    tenant_id: string;
    module_key: ModuleKey | string;
    enabled: number; // 0/1
  };

  const rowsToEntitlements = React.useCallback((rows: ApiTenantModuleRow[]): Entitlements => {
    const base = defaultEntitlements();
    const enabled = { ...base.enabled };

    // Always force core on
    enabled.core = true;

    for (const r of rows || []) {
      const key = String(r.module_key || "") as ModuleKey;
      if (!MODULE_KEYS.includes(key)) continue;
      if (key === "core") continue;
      enabled[key] = Number(r.enabled) === 1;
    }

    return { enabled };
  }, []);


  const reloadEntitlementsFromApi = React.useCallback(
    async (forTenantId: string) => {
      const tid = (forTenantId || "").trim() || "demo";

      try {
        const res = await apiRequest<ApiOk<ApiTenantModuleRow[]> | ApiErr>(
          "GET",
          "/api/tenants/modules",
          { tenantId: tid }
        );

        if ((res as any)?.ok) {
          const rows = ((res as any).data || []) as ApiTenantModuleRow[];
          setEntitlements(rowsToEntitlements(rows));
          return;
        }

        console.warn("Tenant modules API load failed:", (res as any)?.error);
      } catch (e) {
        console.warn("Tenant modules API load failed:", e);
      }

      // fallback: keep safe default (all on) if API fails
      setEntitlements(defaultEntitlements());
    },
    [rowsToEntitlements]
  );

  // (API-backed arrays are also reloaded below via their API effects.)
  useEffect(() => {
    isHydratingRef.current = true;

    // entitlements are DB-backed now
    reloadEntitlementsFromApi(scopeTenantId);

    setCompanyInfo({ legalName: "", updatedAt: new Date().toISOString() });

    setPartners([]);
    setClients([]);
    setProjectsRaw([]);
    setExpenses([]);
    // Subcontracts are API-backed. Do NOT re-hydrate from localStorage on tenant switch,
    // otherwise stale browser data can override DB until API loads or fails silently.
    setSubContractsRaw([]);
    setPaymentsReceived([]);
    setScopeItems([]);
    setSubContractors([]);

    // fallback loads
    setResources([]);
    setWageEntries([]);
    setPartnerDisbursements([]);

    setHydratedScopeId(scopeTenantId);

    setTimeout(() => {
      isHydratingRef.current = false;
    }, 0);
  }, [storageDriver, KEYS, scopeTenantId, reloadEntitlementsFromApi]);

  const canPersist = hydratedScopeId === scopeTenantId && !isHydratingRef.current;

  /* ======================
     Persist (tenant-scoped) — guarded
  ====================== */

  // entitlements are DB-backed; do not persist to localStorage

  useEffect(() => {
    if (!canPersist) return;
    storageDriver.save(KEYS.partners, partners);
  }, [canPersist, storageDriver, KEYS, partners]);

  // keep clients fallback
  useEffect(() => {
    if (!canPersist) return;
    storageDriver.save(KEYS.clients, clients);
  }, [canPersist, storageDriver, KEYS, clients]);

  useEffect(() => {
    if (!canPersist) return;
    storageDriver.save(KEYS.companyInfo, companyInfo);
  }, [canPersist, storageDriver, KEYS, companyInfo]);

  // NOTE: Projects are API-backed; keep as fallback.
  useEffect(() => {
    if (!canPersist) return;
    storageDriver.save(KEYS.projects, projectsRaw);
  }, [canPersist, storageDriver, KEYS, projectsRaw]);

  // NOTE: Expenses are API-backed; keep as fallback.
  useEffect(() => {
    if (!canPersist) return;
    storageDriver.save(KEYS.expenses, expenses);
  }, [canPersist, storageDriver, KEYS, expenses]);

  // NOTE: Subcontracts are API-backed; keep as fallback.
  useEffect(() => {
    if (!canPersist) return;
    storageDriver.save(KEYS.subcontracts, subContractsRaw);
  }, [canPersist, storageDriver, KEYS, subContractsRaw]);

  // NOTE: Payments are API-backed; keep as fallback.
  useEffect(() => {
    if (!canPersist) return;
    storageDriver.save(KEYS.paymentsReceived, paymentsReceived);
  }, [canPersist, storageDriver, KEYS, paymentsReceived]);

  // NOTE: Scope is API-backed; keep as fallback.
  useEffect(() => {
    if (!canPersist) return;
    storageDriver.save(KEYS.scopeItems, scopeItems);
  }, [canPersist, storageDriver, KEYS, scopeItems]);

  // NOTE: subcontractors still local; keep persisted.
  useEffect(() => {
    if (!canPersist) return;
    storageDriver.save(KEYS.subcontractors, subContractors);
  }, [canPersist, storageDriver, KEYS, subContractors]);

  // NOTE: Resources are API-backed; keep as fallback.
  useEffect(() => {
    if (!canPersist) return;
    storageDriver.save(KEYS.resources, resources);
  }, [canPersist, storageDriver, KEYS, resources]);

  // NOTE: Wages are API-backed; keep as fallback.
  useEffect(() => {
    if (!canPersist) return;
    storageDriver.save(KEYS.wageEntries, wageEntries);
  }, [canPersist, storageDriver, KEYS, wageEntries]);

  // NOTE: PartnerDisbursements are API-backed; keep as fallback.
  useEffect(() => {
    if (!canPersist) return;
    storageDriver.save(KEYS.partnerDisbursements, partnerDisbursements);
  }, [canPersist, storageDriver, KEYS, partnerDisbursements]);

  /* ======================
     API Types
  ====================== */


    /* ======================
      ✅ SYSTEM TENANTS (DB-backed list for sysadmin UI dropdown)
      This replaces the local/demo tenant list usage in TopNav.
    ====================== */

    type SystemTenantDto = { id: string; name: string; inviteCode: string };

    const [systemTenants, setSystemTenants] = useState<SystemTenantDto[]>([]);

    const reloadSystemTenantsFromApi = async () => {
      try {
        const res = await apiRequest<any>("GET", "/api/system/tenants", { tenantId: "system" });
        if (res?.ok) setSystemTenants(res.data || []);
      } catch (e) {
        console.warn("System tenants load failed:", e);
      }
    };

    useEffect(() => {
      if (authSession?.role === "admin") reloadSystemTenantsFromApi();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authSession?.role]);


  /* ======================
     ✅ Partners API hydration (D1-backed)
  ====================== */

  type ApiPartner = {
    id: string;
    tenant_id: string;
    name: string;
    created_at?: string;
    updated_at?: string;
  };

  const apiPartnerToUi = (row: ApiPartner): Partner => {
    return {
      id: row.id,
      name: row.name,
    };
  };

  const uiPartnerToApiCreate = (p: Partner) => {
    return {
      id: p.id,
      name: (p.name || "").trim(),
    };
  };

  const uiPartnerToApiPatch = (p: Partner) => {
    return {
      name: (p.name || "").trim(),
    };
  };

  const reloadPartnersFromApi = async () => {
    try {
      const res = await apiRequest<ApiOk<ApiPartner[]> | ApiErr>(
        "GET",
        "/api/partners",
        { tenantId }
      );

      if ((res as any).ok) {
        const rows = (((res as any).data || []) as ApiPartner[]).map(apiPartnerToUi);
        setPartners(rows);
      } else {
        console.warn("Partners API load failed:", (res as any).error);
      }
    } catch (e) {
      console.warn("Partners API load failed:", e);
    }
  };

  useEffect(() => {
    reloadPartnersFromApi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  /* ======================
     ✅ Clients API hydration (D1-backed)
  ====================== */

  type ApiClient = {
    id: string;
    tenant_id: string;
    name: string;
    address?: string | null;
    phone?: string | null;
    email?: string | null;
    notes?: string | null;
    created_at?: string;
    updated_at?: string;
  };

  const apiClientToUi = (row: ApiClient): Client => {
    return {
      id: row.id,
      name: row.name,
      address: row.address ?? undefined,
      phone: row.phone ?? undefined,
      email: row.email ?? undefined,
      notes: row.notes ?? undefined,
    };
  };

  const uiClientToApiCreate = (c: Client) => {
    return {
      id: c.id,
      name: (c.name || "").trim(),
      address: (c.address || "").trim() || null,
      phone: (c.phone || "").trim() || null,
      email: (c.email || "").trim() || null,
      notes: (c.notes || "").trim() || null,
    };
  };

  const uiClientToApiPatch = (c: Client) => {
    return {
      name: (c.name || "").trim(),
      address: (c.address || "").trim() || null,
      phone: (c.phone || "").trim() || null,
      email: (c.email || "").trim() || null,
      notes: (c.notes || "").trim() || null,
    };
  };

  const reloadClientsFromApi = async () => {
    try {
      const res = await apiRequest<ApiOk<ApiClient[]> | ApiErr>("GET", "/api/clients", {
        tenantId,
      });

      if ((res as any).ok) {
        const rows = (((res as any).data || []) as ApiClient[]).map(apiClientToUi);
        setClients(rows);
      } else {
        console.warn("Clients API load failed:", (res as any).error);
      }
    } catch (e) {
      console.warn("Clients API load failed:", e);
    }
  };

  useEffect(() => {
    reloadClientsFromApi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  /* ======================
     Projects API hydration (D1-backed)
  ====================== */

  const reloadProjectsFromApi = async () => {
    try {
      const res = await listProjects(tenantId);
      if ((res as any).ok) {
        const apiRows = ((res as any).data || []) as any[];
        const uiRows = apiRows.map(apiProjectToUi) as Project[];
        setProjectsRaw(uiRows);
      } else {
        console.warn("Projects API load failed:", (res as any).error);
      }
    } catch (e) {
      console.warn("Projects API load failed:", e);
    }
  };

  useEffect(() => {
    reloadProjectsFromApi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  /* ======================
     Expenses API hydration (D1-backed)
  ====================== */

  type ApiExpense = {
    id: string;
    tenant_id: string;
    project_id: string;
    expense_type: "Expense" | "Sub-Contract Payment";
    who_paid: string;
    amount: number;
    payment_method: "Cash" | "Bank";
    date: string;
    notes?: string | null;
    sub_contract_id?: string | null;
    created_at?: string;
    updated_at?: string;
  };

  const apiExpenseToUi = (row: ApiExpense): Expense => {
    return {
      id: row.id,
      projectId: row.project_id,
      expenseType: row.expense_type,
      whoPaid: row.who_paid,
      amount: Number(row.amount) || 0,
      paymentMethod: row.payment_method,
      date: row.date,
      notes: row.notes ?? undefined,
      subContractId: row.sub_contract_id ?? undefined,
    };
  };

  const uiExpenseToApiCreate = (e: Expense) => {
    return {
      id: e.id,
      project_id: e.projectId,
      expense_type: e.expenseType,
      who_paid: e.whoPaid,
      amount: Number(e.amount) || 0,
      payment_method: e.paymentMethod,
      date: e.date,
      notes: e.notes ?? null,
      sub_contract_id: e.subContractId ?? null,
    };
  };

  const uiExpenseToApiPatch = (e: Expense) => {
    return {
      expense_type: e.expenseType,
      who_paid: e.whoPaid,
      amount: Number(e.amount) || 0,
      payment_method: e.paymentMethod,
      date: e.date,
      notes: e.notes ?? null,
      sub_contract_id: e.subContractId ?? null,
    };
  };

  const reloadExpensesFromApi = async () => {
    try {
      const res = await apiRequest<ApiOk<ApiExpense[]> | ApiErr>("GET", "/api/expenses", {
        tenantId,
      });

      if ((res as any).ok) {
        const rows = (((res as any).data || []) as ApiExpense[]).map(apiExpenseToUi);
        setExpenses(rows);
      } else {
        console.warn("Expenses API load failed:", (res as any).error);
      }
    } catch (e) {
      console.warn("Expenses API load failed:", e);
    }
  };

  useEffect(() => {
    reloadExpensesFromApi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  /* ======================
     ✅ Subcontracts API hydration (D1-backed)
     - Fixes “subcontracts saving only in browser”
  ====================== */

  type ApiSubContract = {
    id: string;
    tenant_id: string;
    project_id: string;
    project_address?: string | null;
    subcontractor_id: string;
    title: string;
    scope: string;
    contract_amount: number;
    created_at?: string;
    updated_at?: string;
  };

  const apiSubContractToUi = (row: ApiSubContract): SubContract => {
    return {
      id: row.id,
      projectId: row.project_id,
      projectAddress: row.project_address ?? "",
      subContractorId: row.subcontractor_id,
      title: row.title,
      scope: row.scope,
      contractAmount: Number(row.contract_amount) || 0,
    } as SubContract;
  };

  const uiSubContractToApiCreate = (s: SubContract) => {
    return {
      id: s.id,
      project_id: (s as any).projectId,
      project_address: ((s as any).projectAddress || "").trim() || null,
      subcontractor_id: (s as any).subContractorId,
      title: ((s as any).title || "").trim(),
      scope: ((s as any).scope || "").trim(),
      contract_amount: Number((s as any).contractAmount) || 0,
    };
  };

  const uiSubContractToApiPatch = (s: SubContract) => {
    return {
      project_id: (s as any).projectId,
      project_address: ((s as any).projectAddress || "").trim() || null,
      subcontractor_id: (s as any).subContractorId,
      title: ((s as any).title || "").trim(),
      scope: ((s as any).scope || "").trim(),
      contract_amount: Number((s as any).contractAmount) || 0,
    };
  };

  const reloadSubContractsFromApi = async () => {
    try {
      const res = await apiRequest<ApiOk<ApiSubContract[]> | ApiErr>(
        "GET",
        "/api/subcontracts",
        { tenantId }
      );

      if ((res as any).ok) {
        const rows = (((res as any).data || []) as ApiSubContract[]).map(apiSubContractToUi);
        setSubContractsRaw(rows);
        return;
      }

      console.warn("Subcontracts API load failed:", (res as any).error);
    } catch (e: any) {
      // If you're running the UI from :3000, this usually hits /api on CRA and fails (404),
      // so we want this to be loud.
      console.warn(
        "Subcontracts API load failed. If you're on http://localhost:3000, switch to http://localhost:8788 (wrangler pages dev). Error:",
        e
      );
    }
  };

  useEffect(() => {
    reloadSubContractsFromApi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  /* ======================
     Payments Received API hydration (D1-backed)
  ====================== */

  type ApiPayment = {
    id: string;
    tenant_id: string;
    project_id: string;
    amount: number;
    payment_method: "Cash" | "Bank";
    date: string;
    notes?: string | null;
    created_at?: string;
    updated_at?: string;
  };

  const apiPaymentToUi = (row: ApiPayment): PaymentReceived => {
    return {
      id: row.id,
      projectId: row.project_id,
      amount: Number(row.amount) || 0,
      paymentMethod: row.payment_method,
      date: row.date,
      notes: row.notes ?? undefined,
    };
  };

  const reloadPaymentsFromApi = async () => {
    try {
      const res = await apiRequest<any>("GET", "/api/payments_received", { tenantId });

      if (res?.ok) {
        const rows = (res.data || []) as ApiPayment[];
        setPaymentsReceived(rows.map(apiPaymentToUi));
      } else {
        console.warn("Payments API load failed:", res?.error);
      }
    } catch (e) {
      console.warn("Payments API load failed:", e);
    }
  };

  useEffect(() => {
    reloadPaymentsFromApi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  /* ======================
     Scope API hydration (D1-backed)
     - Current API is per-project: /api/scope?project_id=...
  ====================== */

  type ApiScope = {
    id: string;
    tenant_id: string;
    project_id: string;
    job_area?: string | null;
    description?: string | null;
    estimate: number;
    created_at?: string;
    updated_at?: string;
  };

  const apiScopeToUi = (row: ApiScope): ScopeItem => {
    return {
      id: row.id,
      projectId: row.project_id,
      jobArea: (row.job_area ?? "").toString(),
      description: (row.description ?? "").toString(),
      estimate: Number(row.estimate) || 0,
    };
  };

  const reloadScopeFromApi = async (projectIds: string[]) => {
    const ids = (projectIds || []).filter(Boolean);
    if (!ids.length) {
      setScopeItems([]);
      return;
    }

    try {
      const results = await Promise.allSettled(
        ids.map((pid) =>
          apiRequest<any>(
            "GET",
            `/api/scope?project_id=${encodeURIComponent(pid)}`,
            { tenantId }
          )
        )
      );

      const rows: ApiScope[] = [];
      results.forEach((r) => {
        if (r.status !== "fulfilled") return;
        const res: any = r.value;
        if (res?.ok) rows.push(...((res.data || []) as ApiScope[]));
        else console.warn("Scope API load failed:", res?.error);
      });

      setScopeItems(rows.map(apiScopeToUi));
    } catch (e) {
      console.warn("Scope API load failed:", e);
    }
  };

  useEffect(() => {
    const ids = projectsRaw.map((p: any) => p.id).filter(Boolean);
    reloadScopeFromApi(Array.from(new Set(ids)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, scopeTenantId, projectsRaw.length]);

  /* ======================
     ✅ Resources API hydration (D1-backed)
  ====================== */

  type ApiResource = {
    id: string;
    tenant_id: string;
    name: string;
    role?: string | null;
    phone?: string | null;
    email?: string | null;
    company_name?: string | null;
    notes?: string | null;
    rate?: number | null;
    is_subcontractor?: number | null;
    created_at?: string;
    updated_at?: string;
  };

  const apiResourceToUi = (r: ApiResource): Resource => {
    return {
      id: r.id,
      name: r.name,
      role: r.role ?? undefined,
      phone: r.phone ?? undefined,
      dayRate: Number(r.rate ?? 0) || 0,
      ...({
        email: r.email ?? undefined,
        companyName: r.company_name ?? undefined,
        notes: r.notes ?? undefined,
        isSubcontractor: Number(r.is_subcontractor ?? 0) || 0,
      } as any),
    } as Resource;
  };

  const uiResourceToApiCreate = (r: Resource) => {
    return {
      id: (r as any).id,
      name: (r as any).name ?? "",
      role: (r as any).role ?? "",
      phone: (r as any).phone ?? "",
      email: (r as any).email ?? "",
      company_name: (r as any).companyName ?? "",
      notes: (r as any).notes ?? "",
      rate: Number((r as any).dayRate ?? 0) || 0,
      is_subcontractor: Number((r as any).isSubcontractor ?? 0) || 0,
    };
  };

  const uiResourceToApiPatch = (r: Resource) => {
    return {
      name: (r as any).name ?? "",
      role: (r as any).role ?? "",
      phone: (r as any).phone ?? "",
      email: (r as any).email ?? "",
      company_name: (r as any).companyName ?? "",
      notes: (r as any).notes ?? "",
      rate: Number((r as any).dayRate ?? 0) || 0,
      is_subcontractor: Number((r as any).isSubcontractor ?? 0) || 0,
    };
  };

  const reloadResourcesFromApi = async () => {
    try {
      const res = await apiRequest<ApiOk<ApiResource[]> | ApiErr>("GET", "/api/resources", {
        tenantId,
      });
      if ((res as any).ok) {
        const rows = (((res as any).data || []) as ApiResource[]).map(apiResourceToUi);
        setResources(rows);
      } else {
        console.warn("Resources API load failed:", (res as any).error);
      }
    } catch (e) {
      console.warn("Resources API load failed:", e);
    }
  };

  useEffect(() => {
    reloadResourcesFromApi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  /* ======================
     ✅ Wages API hydration (D1-backed)
  ====================== */

  type ApiWage = {
    id: string;
    tenant_id: string;
    year: number;
    week_number: number;
    resource_id: string;
    project_id: string;

    mon: number;
    tue: number;
    wed: number;
    thu: number;
    fri: number;
    sat: number;
    sun: number;

    days_worked: number;
    total_wages: number;
    adjustments?: number | null;
    description?: string | null;

    paid: number; // 0/1
    paid_date?: string | null;
    paid_method?: "Cash" | "Bank" | null;
    paid_notes?: string | null;

    created_at?: string;
    updated_at?: string;
  };

  const apiWageToUi = (w: ApiWage): WageEntry => {
    return {
      id: w.id,
      year: Number(w.year) || 0,
      weekNumber: Number(w.week_number) || 0,
      resourceId: w.resource_id,
      projectId: w.project_id,

      mon: Number(w.mon) || 0,
      tue: Number(w.tue) || 0,
      wed: Number(w.wed) || 0,
      thu: Number(w.thu) || 0,
      fri: Number(w.fri) || 0,
      sat: Number(w.sat) || 0,
      sun: Number(w.sun) || 0,

      daysWorked: Number(w.days_worked) || 0,
      totalWages: Number(w.total_wages) || 0,
      adjustments: Number(w.adjustments ?? 0) || 0,
      description: w.description ?? undefined,

      paid: Number(w.paid) === 1,
      ...(w.paid_date ? ({ paidDate: w.paid_date } as any) : {}),
      ...(w.paid_method ? ({ paidMethod: w.paid_method } as any) : {}),
      ...(w.paid_notes ? ({ paidNotes: w.paid_notes } as any) : {}),
    } as WageEntry;
  };

  const uiWageToApiCreate = (w: WageEntry) => {
    return {
      id: w.id,
      year: Number(w.year) || 0,
      week_number: Number(w.weekNumber) || 0,
      resource_id: w.resourceId,
      project_id: w.projectId,

      mon: Number(w.mon) || 0,
      tue: Number(w.tue) || 0,
      wed: Number(w.wed) || 0,
      thu: Number(w.thu) || 0,
      fri: Number(w.fri) || 0,
      sat: Number(w.sat) || 0,
      sun: Number(w.sun) || 0,

      days_worked: Number(w.daysWorked) || 0,
      total_wages: Number(w.totalWages) || 0,
      adjustments: Number((w as any).adjustments ?? 0) || 0,
      description: (w as any).description ?? null,

      paid: w.paid ? 1 : 0,
      paid_date: (w as any).paidDate ?? null,
      paid_method: (w as any).paidMethod ?? null,
      paid_notes: (w as any).paidNotes ?? null,
    };
  };

  const uiWageToApiPatch = (w: WageEntry) => {
    return {
      year: Number(w.year) || 0,
      week_number: Number(w.weekNumber) || 0,
      resource_id: w.resourceId,
      project_id: w.projectId,

      mon: Number(w.mon) || 0,
      tue: Number(w.tue) || 0,
      wed: Number(w.wed) || 0,
      thu: Number(w.thu) || 0,
      fri: Number(w.fri) || 0,
      sat: Number(w.sat) || 0,
      sun: Number(w.sun) || 0,

      days_worked: Number(w.daysWorked) || 0,
      total_wages: Number(w.totalWages) || 0,
      adjustments: Number((w as any).adjustments ?? 0) || 0,
      description: (w as any).description ?? null,

      paid: w.paid ? 1 : 0,
      paid_date: (w as any).paidDate ?? null,
      paid_method: (w as any).paidMethod ?? null,
      paid_notes: (w as any).paidNotes ?? null,
    };
  };

  const reloadWagesFromApi = async () => {
    try {
      const res = await apiRequest<ApiOk<ApiWage[]> | ApiErr>("GET", "/api/wages", {
        tenantId,
      });

      if ((res as any).ok) {
        const rows = (((res as any).data || []) as ApiWage[]).map(apiWageToUi);
        setWageEntries(rows);
      } else {
        console.warn("Wages API load failed:", (res as any).error);
      }
    } catch (e) {
      console.warn("Wages API load failed:", e);
    }
  };

  useEffect(() => {
    reloadWagesFromApi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  /* ======================
     ✅ Partner Disbursements API hydration (D1-backed)
  ====================== */

  type ApiPartnerDisbursement = {
    id: string;
    projectId: string;
    partnerId: string;
    amount: number;
    method: "Cash" | "Bank";
    date: string;
    notes?: string | null;
    kind?: "Advance" | "Settlement" | "Other" | null;
    createdAt?: string;
    updatedAt?: string;
  };

  const apiPartnerDisbursementToUi = (row: ApiPartnerDisbursement): PartnerDisbursement => {
    return {
      id: row.id,
      projectId: row.projectId,
      partnerId: row.partnerId,
      amount: Number(row.amount) || 0,
      method: row.method,
      date: row.date,
      notes: row.notes ?? undefined,
      kind: (row.kind ?? "Advance") as any,
    };
  };

  const uiPartnerDisbursementToApiCreate = (d: PartnerDisbursement) => {
    return {
      id: d.id,
      projectId: d.projectId,
      partnerId: d.partnerId,
      amount: Number(d.amount) || 0,
      method: d.method || "Cash",
      date: d.date,
      notes: d.notes ?? null,
      kind: (d.kind ?? "Advance") as any,
    };
  };

  const uiPartnerDisbursementToApiPatch = (d: PartnerDisbursement) => {
    return {
      projectId: d.projectId,
      partnerId: d.partnerId,
      amount: Number(d.amount) || 0,
      method: d.method || "Cash",
      date: d.date,
      notes: d.notes ?? null,
      kind: (d.kind ?? "Advance") as any,
    };
  };

  const reloadPartnerDisbursementsFromApi = async () => {
    try {
      const res = await apiRequest<ApiOk<ApiPartnerDisbursement[]> | ApiErr>(
        "GET",
        "/api/partner_disbursements",
        { tenantId }
      );

      if ((res as any).ok) {
        const rows = (((res as any).data || []) as ApiPartnerDisbursement[]).map(
          apiPartnerDisbursementToUi
        );
        setPartnerDisbursements(rows);
      } else {
        console.warn("Partner disbursements API load failed:", (res as any).error);
      }
    } catch (e) {
      console.warn("Partner disbursements API load failed:", e);
    }
  };

  const reloadProjectTransfersFromApi = async () => {
    try {
      const res = await apiRequest<any>("GET", "/api/project_transfers", { tenantId });
      if (res?.ok) {
        setProjectTransfers((res.data || []).map((row: any) => ({
          id: row.id,
          sourceProjectId: row.source_project_id,
          destinationProjectId: row.destination_project_id,
          amount: Number(row.amount) || 0,
          paymentMethod: row.payment_method,
          date: row.date,
          notes: row.notes ?? undefined,
        })));
      }
    } catch (e) {
      console.warn("Project transfers API load failed:", e);
    }
  };

    // ✅ Force re-hydration from API so ProjectDetailPage totals update immediately
  const refreshProjectData = async (projectId: string) => {
    const pid = (projectId || "").trim();

    await Promise.allSettled([
      reloadProjectsFromApi(),
      reloadSubContractsFromApi(),
      reloadExpensesFromApi(),
      reloadPaymentsFromApi(),
      reloadWagesFromApi(),
      reloadPartnerDisbursementsFromApi(),
      reloadProjectTransfersFromApi(),
      pid ? reloadScopeFromApi([pid]) : Promise.resolve(),
    ]);
  };

  useEffect(() => {
    reloadPartnerDisbursementsFromApi();
    reloadProjectTransfersFromApi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  /* ======================
     Derived
  ====================== */

  const hasModule = (m: ModuleKey) => (m === "core" ? true : Boolean(entitlements.enabled[m]));

  const subContracts: EnrichedSubContract[] = useMemo(() => {
  // If API hasn't loaded yet, we still show whatever is in subContractsRaw (which you clear on tenant switch).
  // This is fine — but keep the guard meaningful for future fallback behavior.
  const source = subContractsRaw;
  return source.map((sc) => {

      const paid = expenses
        .filter(
          (e) =>
            e.expenseType === "Sub-Contract Payment" && e.subContractId === sc.id
        )
        .reduce((sum: number, e) => sum + (Number(e.amount) || 0), 0);

      return {
        ...sc,
        paidToDate: paid,
        balance: (Number((sc as any).contractAmount) || 0) - paid,
      };
    });
  }, [subContractsRaw, expenses]);

  const projects: EnrichedProject[] = useMemo(() => {
    return projectsRaw.map((p) => {
      const totalEstimate = scopeItems
        .filter((s) => s.projectId === p.id)
        .reduce((sum: number, s) => sum + (Number(s.estimate) || 0), 0);

      const received = paymentsReceived
        .filter((pr) => pr.projectId === p.id)
        .reduce((sum: number, pr) => sum + (Number(pr.amount) || 0), 0);

      const balance = totalEstimate - received;

      return {
        ...p,
        totalEstimate,
        received,
        balance,
        status: (p as any).status || "Active",
      };
    });
  }, [projectsRaw, scopeItems, paymentsReceived]);

  const engineResult = useMemo(() => {
    return computeProjectFinancialsEngine({
      projects,
      partners,
      scopeItems,
      paymentsReceived,
      expenses,
      wageEntries,
      partnerDisbursements,
      projectTransfers,
    });
  }, [
    projects,
    partners,
    scopeItems,
    paymentsReceived,
    expenses,
    wageEntries,
    partnerDisbursements,
    projectTransfers,
  ]);

  /* ======================
     Actions
  ====================== */


  // ✅ Partners (API-backed)
  const addPartner = (p: Partner) => {
    (async () => {
      const optimistic: Partner = {
        id: String(p.id || "").trim() || `P-${Date.now()}`,
        name: (p.name || "").trim() || "New Partner",
      };

      // optimistic local add
      setPartners((prev) => {
        if (prev.some((x) => x.id === optimistic.id)) return prev;
        return [...prev, optimistic];
      });

      try {
        const res = await apiRequest<ApiOk<ApiPartner> | ApiErr>(
          "POST",
          "/api/partners",
          { tenantId, body: uiPartnerToApiCreate(optimistic) }
        );

        if (!(res as any).ok) {
          throw new Error((res as any).error || "Failed to create partner");
        }

        // re-sync from API to ensure canonical ordering
        reloadPartnersFromApi();
      } catch (e: any) {
        console.warn("Add partner failed:", e);
        // rollback
        setPartners((prev) => prev.filter((x) => x.id !== optimistic.id));
        alert(e?.message || "Failed to create partner");
      }
    })().catch((e: any) => alert(e?.message || "Failed to create partner"));
  };

  const updatePartner = (p: Partner) => {
    (async () => {
      const next: Partner = {
        ...p,
        name: (p.name || "").trim(),
      };

      // optimistic local update
      setPartners((prev) => prev.map((x) => (x.id === next.id ? next : x)));

      try {
        const res = await apiRequest<ApiOk<ApiPartner> | ApiErr>(
          "PATCH",
          `/api/partners/${encodeURIComponent(next.id)}`,
          { tenantId, body: uiPartnerToApiPatch(next) }
        );

        if (!(res as any).ok) {
          throw new Error((res as any).error || "Failed to update partner");
        }

        // keep local consistent with returned row
        const updated = apiPartnerToUi((res as any).data as ApiPartner);
        setPartners((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
      } catch (e: any) {
        console.warn("Update partner failed:", e);
        alert(e?.message || "Failed to update partner");
        reloadPartnersFromApi();
      }
    })().catch((e: any) => alert(e?.message || "Failed to update partner"));
  };

  const deletePartner = (partnerId: string) => {
    (async () => {
      const id = String(partnerId || "").trim();
      if (!id) return;

      const snap = partners;

      // optimistic local remove
      setPartners((prev) => prev.filter((x) => x.id !== id));

      try {
        const res = await apiRequest<ApiOk<{ id: string }> | ApiErr>(
          "DELETE",
          `/api/partners/${encodeURIComponent(id)}`,
          { tenantId }
        );

        if (!(res as any).ok) {
          throw new Error((res as any).error || "Failed to delete partner");
        }
      } catch (e: any) {
        console.warn("Delete partner failed:", e);
        alert(e?.message || "Failed to delete partner");
        // rollback
        setPartners(snap);
        reloadPartnersFromApi();
      }
    })().catch((e: any) => alert(e?.message || "Failed to delete partner"));
  };

  // ✅ Clients (API-backed)
  const addClient = (c: Client) => {
    (async () => {
      const body = uiClientToApiCreate(c);
      const res = await apiRequest<ApiOk<ApiClient> | ApiErr>("POST", "/api/clients", {
        tenantId,
        body,
      });

      if (!(res as any).ok) {
        alert((res as any).error || "Failed to create client");
        return;
      }

      const created = apiClientToUi((res as any).data as ApiClient);
      setClients((prev) => [created, ...prev]);
    })().catch((e: any) => alert(e?.message || "Failed to create client"));
  };

  const updateClient = (c: Client) => {
    (async () => {
      const body = uiClientToApiPatch(c);
      const res = await apiRequest<ApiOk<ApiClient> | ApiErr>(
        "PATCH",
        `/api/clients/${encodeURIComponent(c.id)}`,
        { tenantId, body }
      );

      if (!(res as any).ok) {
        alert((res as any).error || "Failed to update client");
        return;
      }

      const updated = apiClientToUi((res as any).data as ApiClient);
      setClients((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
    })().catch((e: any) => alert(e?.message || "Failed to update client"));
  };

  const deleteClient = (clientId: string) => {
    const isUsed = projectsRaw.some((p) => p.clientId === clientId);
    if (isUsed) {
      alert("Cannot delete: this client is linked to one or more projects.");
      return;
    }

    (async () => {
      const res = await apiRequest<ApiOk<{ id: string }> | ApiErr>(
        "DELETE",
        `/api/clients/${encodeURIComponent(clientId)}`,
        { tenantId }
      );

      if (!(res as any).ok) {
        alert((res as any).error || "Failed to delete client");
        return;
      }

      setClients((prev) => prev.filter((x) => x.id !== clientId));
    })().catch((e: any) => alert(e?.message || "Failed to delete client"));
  };

  // Projects (✅ API-backed)
  const addProject = (p: Project) => {
    (async () => {
      const body = uiProjectToApi(p);
      const res = await createProject(tenantId, body);
      if (!(res as any).ok) {
        alert((res as any).error || "Failed to create project");
        return;
      }
      const created = apiProjectToUi((res as any).data) as Project;
      setProjectsRaw((prev) => [created, ...prev]);
    })().catch((e: any) => alert(e?.message || "Failed to create project"));
  };

  const updateProject = (p: Project) => {
    (async () => {
      const body = uiProjectToApi(p);
      const res = await updateProjectById(tenantId, p.id, body);
      if (!(res as any).ok) {
        alert((res as any).error || "Failed to update project");
        return;
      }
      const updated = apiProjectToUi((res as any).data) as Project;
      setProjectsRaw((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
    })().catch((e: any) => alert(e?.message || "Failed to update project"));
  };

  const archiveProject = (projectId: string) => {
    const hit = projectsRaw.find((p) => p.id === projectId);
    if (!hit) return;
    updateProject({ ...hit, archived: true } as any);
  };

  const restoreProject = (projectId: string) => {
    const hit = projectsRaw.find((p) => p.id === projectId);
    if (!hit) return;
    updateProject({ ...hit, archived: false } as any);
  };

  // Expenses (✅ API-backed)
  const addExpense = (e: Expense) => {
    (async () => {
      const body = uiExpenseToApiCreate(e);
      const res = await apiRequest<ApiOk<ApiExpense> | ApiErr>("POST", "/api/expenses", {
        tenantId,
        body,
      });

      if (!(res as any).ok) {
        alert((res as any).error || "Failed to create expense");
        return;
      }

      const created = apiExpenseToUi((res as any).data as ApiExpense);
      setExpenses((prev) => [created, ...prev]);
    })().catch((err: any) => alert(err?.message || "Failed to create expense"));
  };

  const updateExpense = (e: Expense) => {
    (async () => {
      const body = uiExpenseToApiPatch(e);
      const res = await apiRequest<ApiOk<ApiExpense> | ApiErr>(
        "PATCH",
        `/api/expenses/${encodeURIComponent(e.id)}`,
        { tenantId, body }
      );

      if (!(res as any).ok) {
        alert((res as any).error || "Failed to update expense");
        return;
      }

      const updated = apiExpenseToUi((res as any).data as ApiExpense);
      setExpenses((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
    })().catch((err: any) => alert(err?.message || "Failed to update expense"));
  };

  const deleteExpense = (expenseId: string) => {
    (async () => {
      const res = await apiRequest<ApiOk<any> | ApiErr>(
        "DELETE",
        `/api/expenses/${encodeURIComponent(expenseId)}`,
        { tenantId }
      );

      if (!(res as any).ok) {
        alert((res as any).error || "Failed to delete expense");
        return;
      }

      setExpenses((prev) => prev.filter((x) => x.id !== expenseId));
    })().catch((err: any) => alert(err?.message || "Failed to delete expense"));
  };

  // ✅ Subcontracts (API-backed)
  const addSubContract = (s: SubContract) => {
    (async () => {
      const body = uiSubContractToApiCreate(s);
      const res = await apiRequest<ApiOk<ApiSubContract> | ApiErr>(
        "POST",
        "/api/subcontracts",
        { tenantId, body }
      );

      if (!(res as any).ok) {
        alert((res as any).error || "Failed to create sub-contract");
        return;
      }

      const created = apiSubContractToUi((res as any).data as ApiSubContract);
      setSubContractsRaw((prev) => [created, ...prev]);
    })().catch((e: any) => alert(e?.message || "Failed to create sub-contract"));
  };

  const updateSubContract = (s: SubContract) => {
    (async () => {
      const body = uiSubContractToApiPatch(s);
      const res = await apiRequest<ApiOk<ApiSubContract> | ApiErr>(
        "PATCH",
        `/api/subcontracts/${encodeURIComponent(s.id)}`,
        { tenantId, body }
      );

      if (!(res as any).ok) {
        alert((res as any).error || "Failed to update sub-contract");
        return;
      }

      const updated = apiSubContractToUi((res as any).data as ApiSubContract);
      setSubContractsRaw((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
    })().catch((e: any) => alert(e?.message || "Failed to update sub-contract"));
  };

  const deleteSubContract = (subContractId: string) => {
    const hasPayments = expenses.some(
      (e) =>
        e.expenseType === "Sub-Contract Payment" && e.subContractId === subContractId
    );
    if (hasPayments) {
      alert(
        "Cannot delete: payments exist for this sub-contract. Edit/delete the related expense entries first."
      );
      return;
    }

    (async () => {
      const res = await apiRequest<ApiOk<{ id: string }> | ApiErr>(
        "DELETE",
        `/api/subcontracts/${encodeURIComponent(subContractId)}`,
        { tenantId }
      );

      if (!(res as any).ok) {
        alert((res as any).error || "Failed to delete sub-contract");
        return;
      }

      setSubContractsRaw((prev) => prev.filter((x) => x.id !== subContractId));
    })().catch((e: any) => alert(e?.message || "Failed to delete sub-contract"));
  };

  // Payments (keep existing behavior for now; API hydration already loads list)
  const addPaymentReceived = (p: PaymentReceived) => setPaymentsReceived((prev) => [...prev, p]);
  const updatePaymentReceived = (p: PaymentReceived) =>
    setPaymentsReceived((prev) => prev.map((x) => (x.id === p.id ? p : x)));
  const deletePaymentReceived = (paymentId: string) =>
    setPaymentsReceived((prev) => prev.filter((x) => x.id !== paymentId));

  // Scope (still local for Phase 1)
  const addScopeItem = (s: ScopeItem) => setScopeItems((prev) => [...prev, s]);
  const updateScopeItem = (s: ScopeItem) =>
    setScopeItems((prev) => prev.map((x) => (x.id === s.id ? s : x)));
  const deleteScopeItem = (scopeItemId: string) =>
    setScopeItems((prev) => prev.filter((x) => x.id !== scopeItemId));

  // Subcontractors (still local)
  const addSubContractor = (s: SubContractor) => setSubContractors((prev) => [...prev, s]);
  const updateSubContractor = (s: SubContractor) =>
    setSubContractors((prev) => prev.map((x) => (x.id === s.id ? s : x)));
  const deleteSubContractor = (subContractorId: string) => {
    const used = subContractsRaw.some((sc) => sc.subContractorId === subContractorId);
    if (used) {
      alert("Cannot delete: this subcontractor is linked to one or more sub-contracts.");
      return;
    }
    setSubContractors((prev) => prev.filter((x) => x.id !== subContractorId));
  };

  // ✅ Resources (API-backed)
  const addResource = (r: Resource) => {
    (async () => {
      const body = uiResourceToApiCreate(r);
      const res = await apiRequest<ApiOk<ApiResource> | ApiErr>("POST", "/api/resources", {
        tenantId,
        body,
      });
      if (!(res as any).ok) {
        alert((res as any).error || "Failed to add resource");
        return;
      }
      const created = apiResourceToUi((res as any).data as ApiResource);
      setResources((prev) => [created, ...prev]);
    })().catch((e: any) => alert(e?.message || "Failed to add resource"));
  };

  const updateResource = (r: Resource) => {
    (async () => {
      const body = uiResourceToApiPatch(r);
      const res = await apiRequest<ApiOk<ApiResource> | ApiErr>(
        "PATCH",
        `/api/resources/${encodeURIComponent(r.id)}`,
        { tenantId, body }
      );
      if (!(res as any).ok) {
        alert((res as any).error || "Failed to update resource");
        return;
      }
      const updated = apiResourceToUi((res as any).data as ApiResource);
      setResources((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
    })().catch((e: any) => alert(e?.message || "Failed to update resource"));
  };

  const deleteResource = (resourceId: string) => {
    const usedInWages = wageEntries.some((w) => w.resourceId === resourceId);
    if (usedInWages) {
      alert("Cannot delete: this resource is linked to one or more wage entries.");
      return;
    }

    (async () => {
      const res = await apiRequest<ApiOk<{}> | ApiErr>(
        "DELETE",
        `/api/resources/${encodeURIComponent(resourceId)}`,
        { tenantId }
      );
      if (!(res as any).ok) {
        alert((res as any).error || "Failed to delete resource");
        return;
      }
      setResources((prev) => prev.filter((x) => x.id !== resourceId));
    })().catch((e: any) => alert(e?.message || "Failed to delete resource"));
  };

  // ✅ Wages (API-backed)
  const addWageEntry = (w: WageEntry) => {
    (async () => {
      const body = uiWageToApiCreate(w);
      const res = await apiRequest<ApiOk<ApiWage> | ApiErr>("POST", "/api/wages", {
        tenantId,
        body,
      });
      if (!(res as any).ok) {
        alert((res as any).error || "Failed to create wage entry");
        return;
      }
      const created = apiWageToUi((res as any).data as ApiWage);
      setWageEntries((prev) => [created, ...prev]);
    })().catch((e: any) => alert(e?.message || "Failed to create wage entry"));
  };

  const updateWageEntry = (w: WageEntry) => {
    (async () => {
      const body = uiWageToApiPatch(w);
      const res = await apiRequest<ApiOk<ApiWage> | ApiErr>(
        "PATCH",
        `/api/wages/${encodeURIComponent(w.id)}`,
        { tenantId, body }
      );
      if (!(res as any).ok) {
        alert((res as any).error || "Failed to update wage entry");
        return;
      }
      const updated = apiWageToUi((res as any).data as ApiWage);
      setWageEntries((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
    })().catch((e: any) => alert(e?.message || "Failed to update wage entry"));
  };

  const deleteWageEntry = (wageId: string) => {
    (async () => {
      const res = await apiRequest<ApiOk<{}> | ApiErr>(
        "DELETE",
        `/api/wages/${encodeURIComponent(wageId)}`,
        { tenantId }
      );
      if (!(res as any).ok) {
        alert((res as any).error || "Failed to delete wage entry");
        return;
      }
      setWageEntries((prev) => prev.filter((x) => x.id !== wageId));
    })().catch((e: any) => alert(e?.message || "Failed to delete wage entry"));
  };

  // ✅ Partner disbursements (API-backed)
  const addPartnerDisbursement = (d: PartnerDisbursement) => {
    (async () => {
      const body = uiPartnerDisbursementToApiCreate(d);
      const res = await apiRequest<ApiOk<ApiPartnerDisbursement> | ApiErr>(
        "POST",
        "/api/partner_disbursements",
        { tenantId, body }
      );
      if (!(res as any).ok) {
        alert((res as any).error || "Failed to create partner disbursement");
        return;
      }
      const created = apiPartnerDisbursementToUi((res as any).data as ApiPartnerDisbursement);
      setPartnerDisbursements((prev) => [created, ...prev]);
    })().catch((e: any) => alert(e?.message || "Failed to create partner disbursement"));
  };

  const updatePartnerDisbursement = (d: PartnerDisbursement) => {
    (async () => {
      const body = uiPartnerDisbursementToApiPatch(d);
      const res = await apiRequest<ApiOk<ApiPartnerDisbursement> | ApiErr>(
        "PATCH",
        `/api/partner_disbursements/${encodeURIComponent(d.id)}`,
        { tenantId, body }
      );
      if (!(res as any).ok) {
        alert((res as any).error || "Failed to update partner disbursement");
        return;
      }
      const updated = apiPartnerDisbursementToUi((res as any).data as ApiPartnerDisbursement);
      setPartnerDisbursements((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
    })().catch((e: any) => alert(e?.message || "Failed to update partner disbursement"));
  };

  const deletePartnerDisbursement = (id: string) => {
    (async () => {
      const res = await apiRequest<ApiOk<{}> | ApiErr>(
        "DELETE",
        `/api/partner_disbursements/${encodeURIComponent(id)}`,
        { tenantId }
      );
      if (!(res as any).ok) {
        alert((res as any).error || "Failed to delete partner disbursement");
        return;
      }
      setPartnerDisbursements((prev) => prev.filter((x) => x.id !== id));
    })().catch((e: any) => alert(e?.message || "Failed to delete partner disbursement"));
  };

  /* Reset helpers */
  const deleteProjectAndRelated = (projectId: string) => {
    setProjectsRaw((prev) => prev.filter((p) => p.id !== projectId));
    setScopeItems((prev) => prev.filter((s) => s.projectId !== projectId));
    setPaymentsReceived((prev) => prev.filter((x) => x.projectId !== projectId));
    setExpenses((prev) => prev.filter((x) => x.projectId !== projectId));
    setSubContractsRaw((prev) => prev.filter((x) => x.projectId !== projectId));
    setWageEntries((prev) => prev.filter((x) => x.projectId !== projectId));
    setPartnerDisbursements((prev) => prev.filter((x) => x.projectId !== projectId));
  };

  const resetAllData = () => {
    const keysToClear = [
      KEYS.clients,
      KEYS.projects,
      KEYS.expenses,
      KEYS.subcontracts,
      KEYS.paymentsReceived,
      KEYS.scopeItems,
      KEYS.subcontractors,
      KEYS.resources,
      KEYS.wageEntries,
      KEYS.partnerDisbursements,
    ];

    storageDriver.clear(keysToClear);

    // local state reset (DB will re-hydrate on reload)
    setClients([]);
    setProjectsRaw([]);
    setExpenses([]);
    setSubContractsRaw([]);
    setPaymentsReceived([]);
    setScopeItems([]);
    setResources([]);
    setWageEntries([]);
    setPartnerDisbursements([]);
    setProjectTransfers([]);

    setSubContractors([
      { id: "SC-001", name: "Gustavo - Quinteros General Construction" },
      { id: "SC-002", name: "Benjamin - B & B Painting" },
      { id: "SC-003", name: "Clenio - Flooring Crew" },
      { id: "SC-004", name: "Mazingo - Tiler" },
    ]);
  };

  return (
    <DataContext.Provider
      value={{
        refreshProjectData,

        tenantId,
        setTenantId,

        authSession,
        setAuthSession,
        clearAuthSession,

        tenants,
        activeTenantId: activeTenant.tenantId,
        activeTenant,

        companyInfo,
        setCompanyInfo,

        createTenant,
        renameTenant,
        deleteTenant,
        switchTenant,

        systemTenants,
        reloadSystemTenantsFromApi,

        createInvite,
        consumeInvite,

        entitlements,
        setEntitlements,
        hasModule,

        partners,
        setPartners,
        addPartner,
        updatePartner,
        deletePartner,

        clients,
        projects,
        expenses,
        subContracts,
        paymentsReceived,
        scopeItems,
        subContractors,

        resources,
        reloadResourcesFromApi,
        reloadScopeFromApi,
        reloadPaymentsFromApi,
        reloadExpensesFromApi,
        reloadWagesFromApi,
        reloadPartnerDisbursementsFromApi,
        reloadProjectTransfersFromApi,
        reloadSubContractsFromApi,

        wageEntries,
        partnerDisbursements,
        projectTransfers,

        projectFinancialsByProject: engineResult.byProjectId,
        projectOrder: engineResult.projectOrder,
        partnerLedgerFinal: engineResult.partnerLedgerFinal,

        addClient,
        updateClient,
        deleteClient,

        addProject,
        updateProject,
        archiveProject,
        restoreProject,

        addExpense,
        updateExpense,
        deleteExpense,

        addSubContract,
        updateSubContract,
        deleteSubContract,

        addPaymentReceived,
        updatePaymentReceived,
        deletePaymentReceived,

        addScopeItem,
        updateScopeItem,
        deleteScopeItem,

        addSubContractor,
        updateSubContractor,
        deleteSubContractor,

        addResource,
        updateResource,
        deleteResource,

        addWageEntry,
        updateWageEntry,
        deleteWageEntry,

        addPartnerDisbursement,
        updatePartnerDisbursement,
        deletePartnerDisbursement,

        resetAllData,
        deleteProjectAndRelated,

        storageDriver,
      }}
    >
      {children}
    </DataContext.Provider>
  );
}

export function useData() {
  const ctx = useContext(DataContext);
  if (!ctx) throw new Error("useData must be used within DataProvider");
  return ctx;
}
